import { Component, OnDestroy, OnInit } from '@angular/core';
import { AccountApiProviderService } from '@core/api';
import { uuid } from '@core/helpers';
import { Store, select } from '@ngrx/store';
import cloneDeep from 'lodash/cloneDeep';
import { Observable, Subject } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { TDropdownParams } from 'src/app/project/components/dropdown/dropdown-service/dropdown-service.consts';
import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { TAccount } from 'src/app/project/modules/account/account.model';
import { ResponseErrorService } from 'src/app/project/modules/errors/response-error.service';
import { TPreferences } from 'src/app/project/modules/preferences/preferences.model';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { TWorkspaceSimpleResponse } from 'src/app/project/view-models/account-simple-response.model';
import { TGridOptions } from '../../../layout/grid/grid-consts';
import { TShare } from '../../../share/share.model';
import { TUser } from '../../user.model';
import { TUserSettingsSitesDropdownModalData } from './user-settings-sites-dropdown/user-settings-sites-dropdown-consts';
import { UserSettingsSitesDropdownComponent } from './user-settings-sites-dropdown/user-settings-sites-dropdown.component';

@Component({
  selector: 'pp-user-settings-sites',
  templateUrl: './user-settings-sites.component.html',
  styleUrls: ['./user-settings-sites.component.scss'],
  standalone: false,
})
export class UserSettingsSitesComponent implements OnInit, OnDestroy {
  private workspaces: TWorkspaceSimpleResponse[] = [];
  preferences$: Observable<TPreferences>;
  preferences: TPreferences;
  user$: Observable<TUser>;
  user: TUser;
  accounts: TAccount[] = [];
  keyword = '';
  filteredWorkspaces: TWorkspaceSimpleResponse[] = [];
  EIconPath = EIconPath;
  buttonId = uuid();

  gridOptions: TGridOptions = {
    headerWidthFull: true,
  };

  private readonly destroy$ = new Subject<void>();

  constructor(
    private store: Store<{
      preferences: TPreferences;
    }>,
    private accountApiProviderService: AccountApiProviderService,
    private responseErrorService: ResponseErrorService,
    private dropdownService: DropdownService,
  ) {
    this.preferences$ = this.store.pipe(select(EStore.PREFERENCES));
  }

  ngOnInit(): void {
    this.preferences$
      .pipe(
        takeUntil(this.destroy$),
        tap((preferences) => {
          this.preferences = cloneDeep(preferences);
        }),
      )
      .subscribe();

    this.fetchWorkspaces();
  }

  private fetchWorkspaces(): void {
    this.accountApiProviderService
      .fetchAccountsSimpleHidden(true)
      .pipe(
        tap((accounts) => {
          accounts.forEach((account) => {
            account.workspaces.forEach((accountWorkspace) => {
              this.workspaces.push({
                ...accountWorkspace,
                accountName: account.name,
              });
            });
          });

          this.filterWorkspaces();
        }),
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to fetch workspaces'),
        ),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  setKeyword(keyword: string): void {
    this.keyword = keyword;

    this.filterWorkspaces();
  }

  toggleDropdown(): void {
    const params: TDropdownParams = {
      callback: (shares: TShare[]) => {
        shares.forEach((share) => {
          const workspace = this.workspaces.find((ws) => ws.workspaceId === share.workspaceId);

          if (workspace) {
            workspace.hidden = share.hidden;
          }
          this.filterWorkspaces();
        });
      },
    };

    this.dropdownService.toggleDropdown<TUserSettingsSitesDropdownModalData>(
      this.buttonId,
      {
        workspaces: this.workspaces,
      },
      UserSettingsSitesDropdownComponent,
      params,
    );
  }

  private filterWorkspaces(): void {
    this.filteredWorkspaces = this.workspaces.filter((workspace) =>
      workspace.name.toLowerCase().includes(this.keyword.toLowerCase()),
    );
  }
}
